import { ModuleDocContent } from "./docsData";

export const learningPathModuleDocs: Record<string, ModuleDocContent> = {
  "path-builder": {
    title: "Learning Path Catalog",
    subtitle: "Browse & Manage Learning Paths",
    color: "indigo",
    badge: "Learning Paths",
    description:
      "Browse, manage, and edit structured learning paths. View the learning paths catalog with stats, search and filter by status, and manage individual paths with drag-and-drop course sequencing.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Learning Path Catalog</strong> provides the learning paths catalog and individual path editor. Browse all paths with stats, filter by status, and manage each path's course sequence with drag-and-drop reordering, status changes, and metadata editing.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/55f327a9-ec29-4a1f-82c6-f38e6e5ab32f.png" alt="Learning Path Catalog" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learning Path Catalog overview</p></div>

      <section>
        <h2>Learning Paths Catalog</h2>

        <h3>Stats Cards</h3>
        <p>Three summary cards in the header:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total</strong></td>
              <td>Total number of learning paths.</td>
            </tr>
            <tr>
              <td><strong>Published</strong></td>
              <td>Paths currently available to learners.</td>
            </tr>
            <tr>
              <td><strong>Drafts</strong></td>
              <td>Paths still being developed.</td>
            </tr>
          </tbody>
        </table>

        <h3>Filters</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search learning paths by name.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>All, Published, Draft, Unpublished.</td>
            </tr>
            <tr>
              <td><strong>View</strong></td>
              <td>Grid or List toggle.</td>
            </tr>
          </tbody>
        </table>

        <h3>Path Cards</h3>
        <p>Each path card displays:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Thumbnail</strong></td>
              <td>AI-generated thumbnail image with status badge overlay.</td>
            </tr>
            <tr>
              <td><strong>Title &amp; Description</strong></td>
              <td>Path name and summary (3-line clamp).</td>
            </tr>
            <tr>
              <td><strong>Tags</strong></td>
              <td>Up to 3 topic tags shown with overflow count.</td>
            </tr>
            <tr>
              <td><strong>Rating</strong></td>
              <td>Average star rating and review count (clickable).</td>
            </tr>
            <tr>
              <td><strong>Metadata</strong></td>
              <td>Target role/skill, course count, enrolled count, difficulty progression.</td>
            </tr>
            <tr>
              <td><strong>Manage button</strong></td>
              <td>Opens the path editor page.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Path Editor</h2>
        <p>The editor page shows the AI-generated banner image, path title, description, slug (with copy-to-clipboard), and tags.</p>

        <h3>Status Management</h3>
        <p>A dropdown in the top-right lets you switch between <strong>Published</strong> (green), <strong>Draft</strong> (amber), and <strong>Unpublished/Archived</strong> (gray). Changes save immediately.</p>

        <h3>Course Sequence</h3>
        <p>The core of the editor is the course sequence section:</p>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Add Course</strong></td>
              <td>Searchable dropdown listing all published courses not already in the path. Select and click Add Course.</td>
            </tr>
            <tr>
              <td><strong>Drag-and-drop reorder</strong></td>
              <td>Grab the grip handle on any course to reorder. Changes auto-save.</td>
            </tr>
            <tr>
              <td><strong>Course details</strong></td>
              <td>Each item shows numbered position, title, difficulty badge (Beginner/Intermediate/Advanced), and expandable description with AI Rationale.</td>
            </tr>
            <tr>
              <td><strong>Remove course</strong></td>
              <td>Trash icon with confirmation modal. Changes auto-save.</td>
            </tr>
            <tr>
              <td><strong>Expand All / Collapse All</strong></td>
              <td>Toggle to show or hide descriptions for all courses at once.</td>
            </tr>
          </tbody>
        </table>

        <h3>Additional Actions</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Preview</strong></td>
              <td>Opens a learner-side preview in a new tab.</td>
            </tr>
            <tr>
              <td><strong>Edit Settings</strong></td>
              <td>Opens a modal to edit path metadata (title, description, etc.).</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Manage AI-generated paths</div>
            <div class="sz-use-case-flow">
              After AI generates a learning path, open the editor to review the course sequence. Reorder courses with drag-and-drop, add any missing courses from the catalog, and remove irrelevant ones. Set the status to Published when ready.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Browse and filter paths</div>
            <div class="sz-use-case-flow">
              Use the catalog to filter by Published status and search by topic. Review ratings and enrollment counts to identify popular paths. Click Manage on any path to edit its course sequence.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use the <strong>Preview</strong> button to see exactly how learners will experience the path before publishing. The AI Rationale shown on each course explains why AI selected it for the sequence.</p>
      </div>

    </div>`,
  },

  "ai-path-gen": {
    title: "AI Path Builder",
    subtitle: "AI-Powered Path Builder",
    color: "indigo",
    badge: "Learning Paths",
    description:
      "Describe a learning goal and let AI automatically generate a complete learning path with title, description, course sequence, multilingual courses, images, and outcomes.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>AI Path Builder</strong> creates complete learning paths from a text description of your learning goal. AI generates the path title, description, course sequence, images, outcomes, and tags automatically.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/18d41319-0590-4448-9c08-e06fa087bd6e.png" alt="AI Path Builder" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI Path Builder interface</p></div>

      <section>
        <h2>How it works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">Describe your learning goal</div>
              <div class="sz-step-desc">
                Enter a description of the learning goal (minimum 20 characters) in the textarea. Optionally click <strong>Enhance with AI</strong> to let AI rewrite and improve your description. Specify the target role or skill.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Configure generation settings</div>
              <div class="sz-step-desc">
                Set the difficulty level (Beginner, Intermediate, Advanced, or Mixed), target audience, industry, and content language.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Generate and monitor progress</div>
              <div class="sz-step-desc">
                Click <strong>Generate Learning Path</strong>. The system processes through 3 stages: <strong>Metadata Generation</strong> (title, description, outcomes, tags), <strong>Image Generation</strong> (banner and thumbnail), and <strong>Course Selection</strong> (selecting and sequencing relevant courses). Each stage shows live progress with elapsed time.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">4</div>
            <div>
              <div class="sz-step-title">Review the generated path</div>
              <div class="sz-step-desc">
                Once complete, view the generated metadata preview (title, description, target role, tags). Click <strong>View Learning Path Details</strong> to open the path editor where you can reorder courses and publish.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>Generation Form</h2>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Describe Your Learning Goal</strong></td>
              <td>Textarea (required, min 20 chars). Use <strong>Enhance with AI</strong> to improve or <strong>Undo</strong> to revert.</td>
            </tr>
            <tr>
              <td><strong>Target Role or Skill</strong></td>
              <td>Required text input (e.g., "Full Stack Developer", "Data Scientist").</td>
            </tr>
            <tr>
              <td><strong>Difficulty Level</strong></td>
              <td>Beginner, Intermediate, Advanced, or Mixed (default: Mixed).</td>
            </tr>
            <tr>
              <td><strong>Targeted Audience</strong></td>
              <td>Optional (e.g., "Working Professionals", "Students").</td>
            </tr>
            <tr>
              <td><strong>Industry</strong></td>
              <td>Optional (e.g., "Technology", "Healthcare").</td>
            </tr>
            <tr>
              <td><strong>Content Language</strong></td>
              <td>Searchable dropdown. Default: All Languages (Recommended).</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Generation Status Page</h2>
        <p>After submitting, you're taken to a status page showing real-time progress:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Metrics Dashboard</strong></td>
              <td>4 cards: Courses count, Completed Stages (X/3), Elapsed Time (live), Progress %.</td>
            </tr>
            <tr>
              <td><strong>3-Stage Timeline</strong></td>
              <td>Expandable cards for Metadata, Image, and Course Selection stages. Each shows status (Pending/In Progress/Completed/Failed) with live timer.</td>
            </tr>
            <tr>
              <td><strong>No Courses Warning</strong></td>
              <td>If AI finds no matching courses, shows AI-suggested courses with importance level and a <strong>Build Course</strong> button for each.</td>
            </tr>
            <tr>
              <td><strong>Retry</strong></td>
              <td>If generation fails, a <strong>Retry Generation</strong> button appears.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Recent Learning Paths</h2>
        <p>Below the generation form, a paginated grid shows previously generated paths. Each card displays the title, creation time, overall status (Generating/Completed/Failed), description, course count, and a 3-stage progress bar showing Metadata, Images, and Courses generation status.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Quick path creation</div>
            <div class="sz-use-case-flow">
              Type a learning goal like "Full-stack web development for beginners". Click Enhance with AI to refine the description. Set difficulty to Mixed and click Generate. The AI creates the entire path in minutes.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Handle missing courses</div>
            <div class="sz-use-case-flow">
              If AI can't find matching courses, review the suggested course list with importance and relevance ratings. Click Build Course on each suggestion to create the missing courses, then regenerate the path.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Write detailed learning goals with specific skills and outcomes for better AI results. Use the <strong>Enhance with AI</strong> button to improve vague descriptions before generating.</p>
      </div>

    </div>`,
  },

  "path-workflow": {
    title: "Corporate Assignment Workflow",
    subtitle: "Automated Path Generation from Job Descriptions",
    color: "indigo",
    badge: "Learning Paths",
    description:
      "Create AI-powered workflows that automatically generate learning paths from job descriptions. Upload a JD or paste text, and AI extracts skills, generates metadata, creates images, and selects courses.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Corporate Assignment Workflow</strong> automates learning path generation from job descriptions. Paste a JD or upload a file (PDF/DOCX/TXT), select a department and role, and AI processes through 5 stages to create a complete learning path with courses.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f5acd42b-746f-4937-bbe2-715f21f0287d.png" alt="Corporate Assignment Workflow" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Corporate assignment workflow interface</p></div>

      <section>
        <h2>Workflow List</h2>

        <h3>Stats Cards</h3>
        <p>Four summary cards in the header:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Workflows</strong></td>
              <td>Total number of workflows created.</td>
            </tr>
            <tr>
              <td><strong>Active</strong></td>
              <td>Currently active workflows.</td>
            </tr>
            <tr>
              <td><strong>Processing</strong></td>
              <td>Workflows currently being processed by AI.</td>
            </tr>
            <tr>
              <td><strong>Failed</strong></td>
              <td>Workflows that encountered errors.</td>
            </tr>
          </tbody>
        </table>

        <h3>Workflow Table</h3>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Workflow Name</strong></td>
              <td>Clickable link to the workflow status page.</td>
            </tr>
            <tr>
              <td><strong>Description</strong></td>
              <td>Workflow description (truncated).</td>
            </tr>
            <tr>
              <td><strong>Group</strong></td>
              <td>Department badge.</td>
            </tr>
            <tr>
              <td><strong>Role</strong></td>
              <td>Target role within the department.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Completed (green), Processing (blue with spinner), Failed (red).</td>
            </tr>
            <tr>
              <td><strong>Activation</strong></td>
              <td>Active/Inactive badge with toggle button (opens confirmation modal).</td>
            </tr>
            <tr>
              <td><strong>Actions</strong></td>
              <td>View status, Edit, Go to learning path (if generated).</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Create Workflow</h2>
        <p>The creation form has three sections:</p>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Basic Information</strong></td>
              <td>Workflow Name (required), Description, Department (required, dropdown), Role (optional, dependent on department).</td>
            </tr>
            <tr>
              <td><strong>Job Description</strong></td>
              <td>Toggle between <strong>Paste Text</strong> (textarea, min 50 words, with word count) or <strong>Upload File</strong> (drag-and-drop for PDF/DOCX/TXT, max 10 MB, with upload progress). Content Language selector.</td>
            </tr>
            <tr>
              <td><strong>Assignment Settings</strong></td>
              <td>Deadline Days (default 30), Mark as Mandatory checkbox, Notes textarea, Send Notification checkbox (default on).</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Workflow Status (5-Stage Pipeline)</h2>
        <p>After creation, the workflow processes through 5 AI stages. The status page shows real-time progress with auto-polling:</p>
        <table>
          <thead>
            <tr>
              <th>Stage</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>1. File Processing</strong></td>
              <td>Extract text from uploaded PDF/DOCX/TXT. Skipped if text was pasted directly.</td>
            </tr>
            <tr>
              <td><strong>2. Skills Extraction</strong></td>
              <td>AI analyzes the job description and extracts required skills (shown as tags when complete).</td>
            </tr>
            <tr>
              <td><strong>3. Metadata Generation</strong></td>
              <td>AI generates path title, description, outcomes, and tags.</td>
            </tr>
            <tr>
              <td><strong>4. Image Generation</strong></td>
              <td>AI generates banner and thumbnail images (previewed side-by-side when complete).</td>
            </tr>
            <tr>
              <td><strong>5. Course Selection</strong></td>
              <td>AI selects and sequences relevant courses from the catalog.</td>
            </tr>
          </tbody>
        </table>
        <p>Each stage shows a status indicator (Pending/In Progress/Completed/Failed/Skipped), live elapsed timer, and expandable details. The metrics dashboard shows Courses count, Completed Stages (X/5), Elapsed Time, and Progress %.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Onboarding path from job description</div>
            <div class="sz-use-case-flow">
              Upload the JD PDF for a new role. Select the department and role. AI extracts required skills and generates a tailored learning path. Activate the workflow to auto-assign the path to new hires in that role.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department-wide skill development</div>
            <div class="sz-use-case-flow">
              Paste a department skill requirements document. Set a 60-day deadline and mark as mandatory. AI generates a learning path covering all required competencies. Activate to assign to the entire department.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Detailed job descriptions produce better learning paths. Include specific technical skills, tools, and competency levels in the JD text for more accurate AI course selection.</p>
      </div>

    </div>`,
  },
};
